/**
 * Page — page-shell layout primitive.
 *
 * Design system mapping (mch-page):
 *   • Root: px-8 py-6 bg-webex-gray-50 dark:bg-webex-gray-950
 *   • Title: 20px font-bold leading-[25px] (matches Webex H1: text-xl/font-bold)
 *   • Info button: 24px circle border webex-gray-500 dark: webex-gray-400
 *   • Subtitle: text-sm text-webex-gray-700 dark: webex-gray-300 mt-1 (Webex 14/400)
 *
 * Composition:
 *   <Page.Root>
 *     <Page.Header>
 *       <Page.Title info="About this page">Workspaces</Page.Title>
 *       <Page.Subtitle>Manage your hot-desking spaces.</Page.Subtitle>
 *       <Page.Actions><Button>Add</Button></Page.Actions>
 *     </Page.Header>
 *     <Page.Body>...</Page.Body>
 *   </Page.Root>
 */
import * as React from "react"
import { cn } from "@lib/cn"

const Root = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn("px-8 py-6 bg-webex-gray-50 dark:bg-webex-gray-950 min-h-full", className)}
      {...props}
    />
  ),
)
Root.displayName = "Page.Root"

function Header({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("mb-4 flex flex-col gap-1", className)} {...props} />
}

interface TitleProps extends React.HTMLAttributes<HTMLHeadingElement> {
  info?: string
}

function Title({ className, info, children, ...props }: TitleProps) {
  return (
    <div className="flex items-center gap-3">
      <h1
        className={cn(
          "m-0 text-xl font-bold leading-[25px] text-webex-gray-1000 dark:text-white",
          className,
        )}
        {...props}
      >
        {children}
      </h1>
      {info && (
        <button
          type="button"
          aria-label={info}
          title={info}
          className={cn(
            "ml-auto inline-grid place-items-center size-6 rounded-full",
            "border border-webex-gray-500 dark:border-webex-gray-400",
            "text-xs text-webex-gray-700 dark:text-webex-gray-300",
            "hover:bg-webex-gray-100 dark:hover:bg-webex-gray-850",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-webex-blue-600 focus-visible:ring-offset-2 focus-visible:ring-offset-webex-gray-50 dark:focus-visible:ring-offset-webex-gray-950",
          )}
        >
          i
        </button>
      )}
    </div>
  )
}

function Subtitle({ className, ...props }: React.HTMLAttributes<HTMLParagraphElement>) {
  return (
    <p
      className={cn("text-sm text-webex-gray-700 dark:text-webex-gray-300 m-0", className)}
      {...props}
    />
  )
}

function Actions({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("mt-3 flex items-center gap-2", className)} {...props} />
}

function Body({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("mt-2", className)} {...props} />
}

export const Page = { Root, Header, Title, Subtitle, Actions, Body }
